import { t } from '@/features/i18n/utils';
import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { SupplementBreadcrumbs } from '@/features/supplements/components/supplement-breadcrumbs';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { Icon } from '@/ui/components/icon';
import { Analytics01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { HeaderBackButton, HeaderChatButton, Header } from '@/ui/components/header';
import {
  MainContainer,
  MainHeader,
  MainHeaderBackButton,
  MainScrollArea,
} from '@/ui/components/main';
import { H1, H2 } from '@/ui/components/heading';
import { useSendChangeAssessmentFeedback } from '@/features/supplements/mutations';
import { changeAssessmentListQuery, changeAssessmentQuery } from '@/features/supplements/queries';
import { useEffect, useState } from 'react';
import { Card } from '@/ui/components/card';
import { Button } from '@/ui/components/button';
import { Textarea } from '@/ui/components/textarea';
import { FeedbackMenu } from '@/features/feedback/components/feedback-menu';
import { FeedbackValue } from '@/features/feedback/types';
import { useDocumentPreview } from '@/features/documents/preview/helpers';
import ReactMarkdown from 'react-markdown';
import { Tabs, TabsList, TabsTab, TabsPanel } from '@/ui/components/tabs';
import { Tooltip, TooltipTrigger, TooltipPopup } from '@/ui/components/tooltip';
import { ChangeAssessmentStatement } from '@/features/supplements/types';
import { ChangeAssessmentPerspectivesSection } from '@/features/supplements/components/change-assessment-perspectives-section';
import { ChangeAssessmentDetailsSection } from '@/features/supplements/components/change-assessment-details-section';
import { IndexingStatusAlerts } from '@/features/documents/components/indexing-status-alerts';
import { useSuspenseQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  AISummaryAlert,
  AISummaryAlertAskAIButton,
  AISummaryAlertCopyButton,
  AISummaryAlertDescription,
  AISummaryAlertTitle,
} from '@/ui/components/ai-summary-alert';
import { useAtom } from 'jotai';
import {
  CommentPanelContent,
  CommentPanelHeader,
  CommentPanelInput,
  CommentPanelTitle,
  CommentPanelXClose,
  ResizableCommentPanel,
} from '@/features/comments/components/comment-panel';
import { SubjectKind } from '@/subjects';
import { useGetCommentCounts } from '@/features/comments/queries';
import { commentPanelOpenAtom } from '@/features/comments/store';
import { CommentButton } from '@/features/comments/components/comment-button';
import { ResizablePanel, ResizablePanelGroup } from '@/ui/components/resizable';
import { ChangeAssessmentBadge } from '@/features/supplements/components/change-assessment-badge';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/contract-management/$supplementId/change-analysis/$assessmentId',
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const assessment = await context.queryClient
      .query({
        ...changeAssessmentQuery(params.supplementId, params.assessmentId),
        staleTime: 'static',
      })
      .catch((error: unknown) => {
        if (isAxiosError(error) && error.response?.status === 404) return null;
        throw error;
      });
    if (!assessment) throw notFound();
  },
});

function RouteComponent() {
  const { workspaceId, projectId, supplementId, assessmentId } = Route.useParams();
  const { commentId } = Route.useSearch();
  const [commentPanelOpen, setCommentPanelOpen] = useAtom(commentPanelOpenAtom);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    if (commentId) {
      setCommentPanelOpen(true);
    }
  }, [commentId]);

  const { countsMap: commentCounts } = useGetCommentCounts({
    type: DOCUMENT_TYPE.contract,
    resourceId: workspaceId,
  });

  const counts = commentCounts?.get(`${SubjectKind.CHANGE_ASSESSMENT}:${assessmentId}`);
  const totalCount = counts?.totalCount ?? 0;
  const unreadCount = counts?.unreadCount ?? 0;

  const { data: assessment } = useSuspenseQuery(changeAssessmentQuery(supplementId, assessmentId));
  useSuspenseQuery(changeAssessmentListQuery(supplementId, workspaceId));

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId/contract-management/$supplementId/change-analysis"
          params={{ projectId, workspaceId, supplementId }}
        />
        <SupplementBreadcrumbs>
          <BreadcrumbLink
            to="/project/$projectId/workspaces/$workspaceId/contract-management/$supplementId/change-analysis"
            params={{ projectId, workspaceId, supplementId }}
          >
            <Icon icon={Analytics01Icon} />
            {t({ en: 'Change analysis', nb: 'Endringsanalyse' })}
          </BreadcrumbLink>
          <BreadcrumbPage>{assessment.title}</BreadcrumbPage>
        </SupplementBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <ResizablePanelGroup>
        <MainScrollArea render={<ResizablePanel className="@container" />}>
          <IndexingStatusAlerts resourceId={supplementId} documentType="supplement" />
          <MainHeader>
            <H1>
              <MainHeaderBackButton
                to="/project/$projectId/workspaces/$workspaceId/contract-management/$supplementId/change-analysis"
                params={{ projectId, workspaceId, supplementId }}
              />
              {assessment.title}
            </H1>
            <CommentButton
              variant="secondary"
              className="max-md:w-full"
              onClick={() => setCommentPanelOpen((prev) => !prev)}
              unreadCount={unreadCount}
              totalCount={totalCount}
            >
              {t({ en: 'Comments', nb: 'Kommentarer' })}
            </CommentButton>
          </MainHeader>
          <MainContainer>
            <AiAssessmentSection />
            <ChangeAssessmentDetailsSection />
            <ChangeAssessmentPerspectivesSection />
            <KeyArgumentsSection />
            <FeedbackCard
              feedback={assessment.isPositiveFeedback}
              userReview={assessment.userReview}
              assessmentId={assessmentId}
            />
          </MainContainer>
        </MainScrollArea>
        <ResizableCommentPanel
          open={commentPanelOpen}
          onOpenChange={setCommentPanelOpen}
          className="border-l"
          type={DOCUMENT_TYPE.contract}
          resourceId={workspaceId}
          subjectKind={SubjectKind.CHANGE_ASSESSMENT}
          subjectId={assessmentId}
          subjectLocation={{ supplementId }}
        >
          <CommentPanelHeader>
            <CommentPanelTitle />
            <CommentPanelXClose onClick={() => setCommentPanelOpen(false)} />
          </CommentPanelHeader>
          <CommentPanelContent />
          <CommentPanelInput className="p-3 pt-0" />
        </ResizableCommentPanel>
      </ResizablePanelGroup>
    </>
  );
}

function AiAssessmentSection() {
  const { assessmentId, supplementId } = Route.useParams();
  const { data } = useSuspenseQuery(changeAssessmentQuery(supplementId, assessmentId));

  return (
    <AISummaryAlert text={data.aiAssessment} className="mb-5">
      <AISummaryAlertTitle>
        <AISummaryAlertCopyButton />
        <AISummaryAlertAskAIButton />
      </AISummaryAlertTitle>
      <AISummaryAlertDescription>
        <ChangeAssessmentBadge className="font-medium mb-3 mt-1" verdict={data.verdict} />
      </AISummaryAlertDescription>
    </AISummaryAlert>
  );
}

function KeyArgumentsSection() {
  const { assessmentId, supplementId } = Route.useParams();
  const { data: assessment } = useSuspenseQuery(changeAssessmentQuery(supplementId, assessmentId));

  const contractStatements = assessment.mainContractStatements;
  const changeRecordStatements = assessment.changeRecordStatements;
  const otherDocsStatements = assessment.otherStatements;

  const tabs = [
    {
      label: t({
        en: `Signed Contract (${contractStatements.length})`,
        nb: `Signert kontrakt (${contractStatements.length})`,
      }),
      value: 'contract',
      statements: contractStatements,
    },
    {
      label: t({
        en: `Change records (${changeRecordStatements.length})`,
        nb: `Endringsposter (${changeRecordStatements.length})`,
      }),
      value: 'change_records',
      statements: changeRecordStatements,
    },
    {
      label: t({
        en: `Other Documents (${otherDocsStatements.length})`,
        nb: `Andre dokumenter (${otherDocsStatements.length})`,
      }),
      value: 'other',
      statements: otherDocsStatements,
    },
  ];

  return (
    <>
      <H2 className="mb-3 mt-5">{t({ en: 'Key arguments', nb: 'Kontekst' })}</H2>
      <Tabs defaultValue="contract" className="w-full">
        <TabsList>
          {tabs.map((tab) => (
            <TabsTab key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTab>
          ))}
        </TabsList>
        {tabs.map((tab) => (
          <KeyArgumentsTab key={tab.value} value={tab.value} statements={tab.statements} />
        ))}
      </Tabs>
    </>
  );
}

function KeyArgumentsTab({
  value,
  statements,
}: {
  value: string;
  statements: ChangeAssessmentStatement[];
}) {
  const openPreview = useDocumentPreview();

  return (
    <TabsPanel value={value} className="pt-4">
      {statements.length > 0 ? (
        <ul className="list-disc list-inside space-y-2 marker:text-muted mb-1">
          {statements.map((statement, idx) => {
            return (
              <li className="text-sm" key={statement.id}>
                {statement.statement}
                {statement.docId && statement.source && (
                  <Tooltip>
                    <TooltipTrigger
                      className="ml-1 disabled:opacity-75 bg-blue-3 hover:bg-blue-4 transition-colors cursor-pointer text-blue-11 rounded-md size-5 inline-flex items-center justify-center text-xs font-semibold"
                      onClick={() =>
                        openPreview({ documentId: statement.docId, source: statement.source })
                      }
                    >
                      {idx + 1}
                    </TooltipTrigger>
                    <TooltipPopup variant="popup" className="max-w-52">
                      <div className="prose">
                        <ReactMarkdown>{statement.source}</ReactMarkdown>
                      </div>
                    </TooltipPopup>
                  </Tooltip>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-muted italic">
          {t({ en: 'No relevant data found.', nb: 'Ingen relevante data funnet.' })}
        </p>
      )}
    </TabsPanel>
  );
}

function FeedbackCard({
  feedback,
  userReview,
  assessmentId,
}: {
  feedback: FeedbackValue;
  userReview: string | null;
  assessmentId: string;
}) {
  const [textReview, setTextReview] = useState('');
  const [localFeedback, setLocalFeedback] = useState<FeedbackValue>(feedback);

  const sendFeedback = useSendChangeAssessmentFeedback();

  const showReviewInput = !userReview || feedback === null || localFeedback !== feedback;

  const disableBtn =
    (localFeedback === null && feedback === null) ||
    (localFeedback === feedback && !(showReviewInput && textReview.trim() !== ''));

  return (
    <Card className="space-y-4 max-w-md mt-6 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm -mt-1 font-medium">
            {t({ en: 'Rate Volve AI', nb: 'Vurder Volve AI' })}
          </h3>
          <p className="text-xs text-muted">
            {t({
              en: 'Is this result helpful / accurate?',
              nb: 'Er dette resultatet nyttig / nøyaktig?',
            })}
          </p>
        </div>
        <div className="flex gap-0.5">
          <FeedbackMenu
            isLoading={sendFeedback.isPending}
            onClick={setLocalFeedback}
            feedback={localFeedback}
          />
        </div>
      </div>
      {showReviewInput && (
        <>
          <Textarea
            value={textReview}
            onChange={(e) => setTextReview(e.target.value)}
            placeholder={t({
              en: 'Please help understand why?',
              nb: 'Vennligst hjelp oss med å forstå hvorfor?',
            })}
            minRows={3}
          />

          <div className="flex justify-end">
            <Button
              onClick={() => {
                if (localFeedback === null) return;
                sendFeedback.mutate(
                  {
                    assessmentId,
                    payload: {
                      isPositiveFeedback: localFeedback,
                      userReview: textReview,
                    },
                  },
                  { onSuccess: () => setTextReview('') },
                );
              }}
              disabled={disableBtn}
              pending={sendFeedback.isPending}
            >
              {t({ en: 'Give feedback', nb: 'Gi tilbakemelding' })}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
